import type { JSX } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useUsageAnalyticsQuery } from '@/hooks/useIssueReportQueries';
import { formatDateTime } from '@/lib/issue-reporting/display';

function Stat({ label, value }: { readonly label: string; readonly value: string | number }): JSX.Element {
  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

/** Humanize a camelCase entity/key into Title Case ("workOrders" → "Work Orders"). */
function humanize(key: string): string {
  const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function UsagePanel(): JSX.Element {
  const { data, isLoading, isError } = useUsageAnalyticsQuery();

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="usage-loading">
        Loading usage…
      </p>
    );
  }
  if (isError || data === undefined) {
    return (
      <p className="text-sm text-destructive" data-testid="usage-error">
        Failed to load usage analytics.
      </p>
    );
  }

  return (
    <div className="space-y-6" data-testid="usage-panel">
      <p className="text-xs text-muted-foreground">
        Snapshot generated {formatDateTime(data.generatedAtUtc)}.
      </p>

      <Card>
        <CardHeader>
          <CardTitle>Users &amp; logins</CardTitle>
          <CardDescription>Account totals and login activity from the login history.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Total users" value={data.users.total} />
            <Stat label="Active users" value={data.users.active} />
            <Stat label="Inactive users" value={data.users.inactive} />
            <Stat label="Logins (24h)" value={data.logins.successfulLast24h} />
            <Stat label="Failed logins (24h)" value={data.logins.failedLast24h} />
            <Stat label="Logins (7d)" value={data.logins.successfulLast7d} />
            <Stat label="Active users (7d)" value={data.logins.distinctUsersLast7d} />
            <Stat label="Active users (30d)" value={data.logins.distinctUsersLast30d} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Records</CardTitle>
          <CardDescription>Live row counts for key entities.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Object.entries(data.entityRowCounts).map(([key, count]) => (
              <Stat key={key} label={humanize(key)} value={count} />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Issues</CardTitle>
          <CardDescription>Reported issues by status and severity.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Total issues" value={data.issues.total} />
            <Stat label="Open issues" value={data.issues.open} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">By status</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.issues.byStatus).map(([k, v]) => (
                  <span key={k} className="rounded-md border border-border px-2 py-1 text-xs">
                    {humanize(k)}: <span className="font-semibold">{v}</span>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">By severity</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.issues.bySeverity).map(([k, v]) => (
                  <span key={k} className="rounded-md border border-border px-2 py-1 text-xs">
                    {humanize(k)}: <span className="font-semibold">{v}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent logins</CardTitle>
          <CardDescription>Last {data.logins.recentLogins.length} login attempts.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table data-testid="recent-logins-table">
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.logins.recentLogins.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="py-6 text-center text-sm text-muted-foreground">
                      No login history.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.logins.recentLogins.map((l, i) => (
                    <TableRow key={`${l.username}-${l.loginTime}-${String(i)}`}>
                      <TableCell className="text-sm">{l.username}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatDateTime(l.loginTime)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {l.isSuccessful ? (
                          <span className="text-green-600">Success</span>
                        ) : (
                          <span className="text-destructive">
                            Failed{l.failureReason !== null ? ` — ${l.failureReason}` : ''}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
